"use client"

import { Head, Link } from "@inertiajs/react"
import { ArrowRight } from "@untitledui/icons"
import { useMemo, useState } from "react"

import {
  examplesRegistry,
  getAllExamples,
  getExampleCount,
  getVariantList,
} from "@/components/ui-kit/untitled/_examples/_registry"
import UuiKitDemosLayout from "@/layouts/uui-kit-demos-layout"

import { DemoSidebar } from "./_components/demo-sidebar"

import { Badge } from "@uui/base/badges/badges"

type CategoryFilter = "all" | "application" | "marketing" | "shared"
type GroupFilter = string | "all"

const categoryColors = {
  application: "gray",
  marketing: "brand",
  shared: "success",
} as const

export default function ExamplesIndex() {
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all")
  const [groupFilter, setGroupFilter] = useState<GroupFilter>("all")

  const allExamples = useMemo(() => getAllExamples(), [])
  const totalCount = getExampleCount()

  const availableGroups = useMemo(() => {
    if (categoryFilter === "all") {
      return Object.entries(examplesRegistry).flatMap(([catKey, cat]) =>
        Object.entries(cat.groups).map(([groupKey, group]) => ({
          key: groupKey,
          name: group.name,
          category: catKey,
        })),
      )
    }
    const category = examplesRegistry[categoryFilter]
    return Object.entries(category.groups).map(([groupKey, group]) => ({
      key: groupKey,
      name: group.name,
      category: categoryFilter,
    }))
  }, [categoryFilter])

  const filteredExamples = useMemo(() => {
    return allExamples.filter((example) => {
      if (categoryFilter !== "all" && example.category !== categoryFilter)
        return false
      if (groupFilter !== "all" && example.group !== groupFilter) return false
      return true
    })
  }, [allExamples, categoryFilter, groupFilter])

  const groupedSummary = useMemo(() => {
    const groups: Record<
      string,
      {
        category: string
        categoryName: string
        groupName: string
        groupKey: string
        count: number
        description: string
        firstVariant: string
      }
    > = {}

    for (const example of filteredExamples) {
      const key = `${example.category}/${example.group}`
      if (!groups[key]) {
        const groupData =
          examplesRegistry[example.category]?.groups[example.group]
        const firstVariant = groupData ? getVariantList(groupData)[0] : "01"
        groups[key] = {
          category: example.category,
          categoryName: example.categoryName,
          groupName: example.groupName,
          groupKey: example.group,
          count: 0,
          description: example.description,
          firstVariant,
        }
      }
      groups[key].count++
    }

    return Object.entries(groups).map(([key, value]) => ({ key, ...value }))
  }, [filteredExamples])

  return (
    <>
      <Head title="Examples Gallery - UUI Demos" />

      <div className="bg-primary flex min-h-screen flex-col lg:flex-row">
        <DemoSidebar />

        <main className="flex min-w-0 flex-1 flex-col gap-8 pt-8 pb-12">
          <div className="flex flex-col justify-between gap-4 px-4 lg:flex-row lg:items-start lg:px-8">
            <div>
              <p className="text-primary lg:text-display-xs text-xl font-semibold">
                Examples Gallery
              </p>
              <p className="text-tertiary mt-1 text-sm">
                Browse all {totalCount} Untitled UI examples for QA review
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge color="success" size="md">
                {filteredExamples.length} examples
              </Badge>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 px-4 lg:px-8">
            <div className="bg-secondary flex gap-1 rounded-lg p-1">
              {(["all", "application", "marketing", "shared"] as const).map(
                (cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setCategoryFilter(cat)
                      setGroupFilter("all")
                    }}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${categoryFilter === cat ? "bg-primary text-primary shadow-sm" : "text-secondary hover:text-primary"}`}
                  >
                    {cat === "all"
                      ? "All Categories"
                      : cat === "shared"
                        ? "Shared Pages"
                        : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ),
              )}
            </div>

            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="border-secondary bg-primary text-primary focus:border-brand-solid focus:ring-brand-solid rounded-lg border px-3 py-1.5 text-sm focus:ring-1 focus:outline-none"
            >
              <option value="all">All Groups</option>
              {availableGroups.map((group) => (
                <option
                  key={`${group.category}/${group.key}`}
                  value={group.key}
                >
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-4 px-4 sm:grid-cols-2 lg:grid-cols-3 lg:px-8 xl:grid-cols-4">
            {groupedSummary.map((group) => (
              <div
                key={group.key}
                className="bg-primary ring-secondary flex flex-col gap-3 rounded-xl p-4 shadow-xs ring-1 ring-inset"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-primary font-semibold">
                      {group.groupName}
                    </p>
                    <p className="text-tertiary text-sm">
                      {group.count} variants
                    </p>
                  </div>
                  <Badge
                    color={
                      categoryColors[
                        group.category as keyof typeof categoryColors
                      ]
                    }
                    size="sm"
                  >
                    {group.categoryName}
                  </Badge>
                </div>
                <p className="text-secondary line-clamp-2 text-sm">
                  {group.description}
                </p>
                <Link
                  href={`/uui-demos/examples/${group.category}/${group.groupKey}/${group.firstVariant}`}
                  className="text-brand-secondary hover:text-brand-secondary_hover inline-flex items-center gap-1 text-sm font-medium"
                >
                  View examples
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          <div className="flex w-full flex-col gap-4 px-4 lg:gap-6 lg:px-8">
            <div className="flex justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <p className="text-primary text-lg font-semibold">
                  All Examples
                </p>
                <p className="text-tertiary text-sm">
                  Click any example to view the full implementation
                </p>
              </div>
            </div>

            <div className="bg-primary ring-secondary rounded-xl shadow-xs ring-1 ring-inset">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-secondary border-b">
                      <th className="text-tertiary px-6 py-3 text-left text-xs font-medium tracking-wider uppercase">
                        Category
                      </th>
                      <th className="text-tertiary px-6 py-3 text-left text-xs font-medium tracking-wider uppercase">
                        Group
                      </th>
                      <th className="text-tertiary px-6 py-3 text-left text-xs font-medium tracking-wider uppercase">
                        Variant
                      </th>
                      <th className="text-tertiary px-6 py-3 text-left text-xs font-medium tracking-wider uppercase">
                        Description
                      </th>
                      <th className="text-tertiary px-6 py-3 text-left text-xs font-medium tracking-wider uppercase">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-secondary divide-y">
                    {filteredExamples.slice(0, 50).map((example) => (
                      <tr
                        key={example.path}
                        className="hover:bg-secondary transition-colors"
                      >
                        <td className="px-6 py-4">
                          <Badge
                            color={
                              categoryColors[
                                example.category as keyof typeof categoryColors
                              ]
                            }
                            size="sm"
                          >
                            {example.categoryName}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-primary font-medium">
                            {example.groupName}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-secondary font-mono text-sm">
                            {example.variantLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-secondary">
                            {example.description}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Link
                            href={example.path}
                            className="text-brand-secondary hover:text-brand-secondary_hover inline-flex items-center gap-1 text-sm font-medium"
                          >
                            View
                            <ArrowRight size={16} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-secondary flex items-center justify-between border-t px-6 py-4">
                <p className="text-tertiary text-sm">
                  Showing {Math.min(50, filteredExamples.length)} of{" "}
                  {filteredExamples.length} examples
                  {filteredExamples.length > 50 &&
                    " (use filters to narrow down)"}
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}

ExamplesIndex.layout = [UuiKitDemosLayout]
